/* eslint-disable no-console */
/*
    This file uses code from three.js
    Original Copyright © 2010-2025 three.js authors
    Source: https://github.com/mrdoob/three.js
    License: MIT (see Frontend/src/THIRD-PARTY-LICENSES/threejs-MIT.txt)
*/

import { Frustum, Vector3, Matrix4, Box3 } from 'three';

// Maximum of geometry elements (vertices or triangles) that are checked per object
const MAX_GEOMETRY_COUNT = 2048;

// Maximum of vertices that are checked for the RTL selection
const MAX_VERTEX_COUNT = 96;

// Maximum of triangles that are checked per object for the RTL selection
const MAX_TRIANGLE_COUNT = 512;

const _tmpPoint = new Vector3();
const _tmpBoxPoints = Array.from({ length: 8 }, () => new Vector3());
const _worldMatrix = new Matrix4();
const _tmpWorldBox = new Box3();

// The 12 edges of a box by corner-index pairs
//  0:(xMin,yMin,zMin) 1:(xMin,yMin,zMax) 2:(xMin,yMax,zMin) 3:(xMin,yMax,zMax)
//  4:(xMax,yMin,zMin) 5:(xMax,yMin,zMax) 6:(xMax,yMax,zMin) 7:(xMax,yMax,zMax)
const _obbEdgePairs = [
    0, 1, 0, 2, 0, 4,
    1, 3, 1, 5,
    2, 3, 2, 6,
    3, 7,
    4, 5, 4, 6,
    5, 7,
    6, 7
];

const _vecNear = new Vector3();
const _vecTopLeft = new Vector3();
const _vecTopRight = new Vector3();
const _vecDownRight = new Vector3();
const _vecDownLeft = new Vector3();

const _vecFarTopLeft = new Vector3();
const _vecFarTopRight = new Vector3();
const _vecFarDownRight = new Vector3();
const _vecFarDownLeft = new Vector3();

const _vectemp1 = new Vector3();
const _vectemp2 = new Vector3();
const _vectemp3 = new Vector3();

const _matrix = new Matrix4();

// Pre-allocated buffer for the OBB convex hull (Jarvis march / gift wrapping)
const _convexHullBuffer = new Int8Array(8);
let _convexHullSize = 0;

/**
 * This class can be used to select 3D objects in a scene with a selection box.
 * It is recommended to visualize the selected area with the help of {@link SelectionHelper}.
 *
 * ```js
 * const selectionBox = new SelectionBox( camera, scene );
 * const selectedObjects = selectionBox.select( startPoint, endPoint );
 * ```
 *
 * @three_import import { SelectionBox } from 'three/addons/interactive/SelectionBox.js';
 */
class SelectionBox {
    /**
     * Constructs a new selection box.
     *
     * @param {Camera} camera - The camera the scene is rendered with.
     * @param {Scene} scene - The scene.
     * @param {number} [deep=Number.MAX_VALUE] - How deep the selection frustum of perspective cameras should extend.
     */
    constructor(camera, scene, deep = Number.MAX_VALUE) {
        /**
         * The camera the scene is rendered with.
         *
         * @type {Camera}
         */
        this.camera = camera;

        /**
         * The camera the scene is rendered with.
         *
         * @type {Scene}
         */
        this.scene = scene;

        /**
         * The start point of the selection.
         *
         * @type {Vector3}
         */
        this.startPoint = new Vector3();

        /**
         * The end point of the selection.
         *
         * @type {Vector3}
         */
        this.endPoint = new Vector3();

        /**
         * The selected 3D objects.
         *
         * @type {Array<Object3D>}
         */
        this.collection = [];

        /**
         * The selected instance IDs of instanced meshes.
         *
         * @type {Record<string, number[]>}
         */
        this.instances = {};
        /**
         * The selected batches of batched meshes.
         *
         * @type {Record<string, number[]>}
         */
        this.batches = {};

        /**
         * How deep the selection frustum of perspective cameras should extend.
         *
         * @type {number}
         * @default Number.MAX_VALUE
         */
        this.deep = deep;

        /**
         * The direction of the selection (left to right or right to left).
         *
         * @type {boolean}
         */
        this.isLeftToRight = true;

        /**
         * The frustum used for the selection.
         *
         * @type {Frustum}
         */
        this.frustum = new Frustum();

        // 2D selection-box bounds in NDC, updated each select() call
        this._selectionMinX = 0;
        this._selectionMaxX = 0;
        this._selectionMinY = 0;
        this._selectionMaxY = 0;
    }

    /**
     * This method selects 3D objects in the scene based on the given start
     * and end point. If no parameters are provided, the method uses the start
     * and end values of the respective members.
     *
     * @param {Vector3} [startPoint] - The start point.
     * @param {Vector3} [endPoint] - The end point.
     * @return {Array<Object3D>} The selected 3D objects.
     */
    select(startPoint, endPoint) {
        this.startPoint = startPoint || this.startPoint;
        this.endPoint = endPoint || this.endPoint;
        this.isLeftToRight = this.startPoint.x < this.endPoint.x;
        this.collection = [];

        this._updateFrustum(this.startPoint, this.endPoint);

        // Pre-compute 2D selection-box bounds in NDC
        this._selectionMinX = Math.min(this.startPoint.x, this.endPoint.x);
        this._selectionMaxX = Math.max(this.startPoint.x, this.endPoint.x);
        this._selectionMinY = Math.min(this.startPoint.y, this.endPoint.y);
        this._selectionMaxY = Math.max(this.startPoint.y, this.endPoint.y);

        this._searchChildInFrustum(this.frustum, this.scene);

        return this.collection;
    }

    _updateFrustum(startPoint, endPoint) {
        startPoint = startPoint || this.startPoint;
        endPoint = endPoint || this.endPoint;

        const minX = Math.min(startPoint.x, endPoint.x);
        const maxX = Math.max(startPoint.x, endPoint.x);
        const topY = Math.max(startPoint.y, endPoint.y);
        const bottomY = Math.min(startPoint.y, endPoint.y);

        // Avoid invalid frustum
        if (minX === maxX) {
            endPoint = endPoint.clone();
            endPoint.x += Number.EPSILON;
        }

        if (topY === bottomY) {
            endPoint = endPoint.clone();
            endPoint.y += Number.EPSILON;
        }

        this.camera.updateProjectionMatrix();
        this.camera.updateMatrixWorld();

        if (this.camera.isPerspectiveCamera) {
            _tmpPoint.set(minX, topY, 0);

            _vecNear.setFromMatrixPosition(this.camera.matrixWorld);
            _vecTopLeft.copy(_tmpPoint);
            _vecTopRight.set(maxX, topY, 0);
            _vecDownRight.set(maxX, bottomY, 0);
            _vecDownLeft.set(minX, bottomY, 0);

            _vecTopLeft.unproject(this.camera);
            _vecTopRight.unproject(this.camera);
            _vecDownRight.unproject(this.camera);
            _vecDownLeft.unproject(this.camera);

            _vectemp1.copy(_vecTopLeft).sub(_vecNear);
            _vectemp2.copy(_vecTopRight).sub(_vecNear);
            _vectemp3.copy(_vecDownRight).sub(_vecNear);
            _vectemp1.normalize();
            _vectemp2.normalize();
            _vectemp3.normalize();

            _vectemp1.multiplyScalar(this.deep);
            _vectemp2.multiplyScalar(this.deep);
            _vectemp3.multiplyScalar(this.deep);
            _vectemp1.add(_vecNear);
            _vectemp2.add(_vecNear);
            _vectemp3.add(_vecNear);

            const planes = this.frustum.planes;
            planes[0].setFromCoplanarPoints(_vecNear, _vecTopLeft, _vecTopRight);
            planes[1].setFromCoplanarPoints(_vecNear, _vecTopRight, _vecDownRight);
            planes[2].setFromCoplanarPoints(_vecDownRight, _vecDownLeft, _vecNear);
            planes[3].setFromCoplanarPoints(_vecDownLeft, _vecTopLeft, _vecNear);
            planes[4].setFromCoplanarPoints(_vecTopRight, _vecDownRight, _vecDownLeft);
            planes[5].setFromCoplanarPoints(_vectemp3, _vectemp2, _vectemp1);
            planes[5].normal.multiplyScalar(-1);
        } else if (this.camera.isOrthographicCamera) {
            const left = Math.min(startPoint.x, endPoint.x);
            const top = Math.max(startPoint.y, endPoint.y);
            const right = Math.max(startPoint.x, endPoint.x);
            const down = Math.min(startPoint.y, endPoint.y);

            _vecTopLeft.set(left, top, -1);
            _vecTopRight.set(right, top, -1);
            _vecDownRight.set(right, down, -1);
            _vecDownLeft.set(left, down, -1);

            _vecFarTopLeft.set(left, top, 1);
            _vecFarTopRight.set(right, top, 1);
            _vecFarDownRight.set(right, down, 1);
            _vecFarDownLeft.set(left, down, 1);

            _vecTopLeft.unproject(this.camera);
            _vecTopRight.unproject(this.camera);
            _vecDownRight.unproject(this.camera);
            _vecDownLeft.unproject(this.camera);

            _vecFarTopLeft.unproject(this.camera);
            _vecFarTopRight.unproject(this.camera);
            _vecFarDownRight.unproject(this.camera);
            _vecFarDownLeft.unproject(this.camera);

            const planes = this.frustum.planes;

            planes[0].setFromCoplanarPoints(_vecTopLeft, _vecFarTopLeft, _vecFarTopRight);
            planes[1].setFromCoplanarPoints(_vecTopRight, _vecFarTopRight, _vecFarDownRight);
            planes[2].setFromCoplanarPoints(_vecFarDownRight, _vecFarDownLeft, _vecDownLeft);
            planes[3].setFromCoplanarPoints(_vecFarDownLeft, _vecFarTopLeft, _vecTopLeft);
            planes[4].setFromCoplanarPoints(_vecTopRight, _vecDownRight, _vecDownLeft);
            planes[5].setFromCoplanarPoints(_vecFarDownRight, _vecFarTopRight, _vecFarTopLeft);
            planes[5].normal.multiplyScalar(-1);
        } else {
            console.error('THREE.SelectionBox: Unsupported camera type.');
        }
    }

    /**
     * Sets the temporary bounding box points to the 8 OBB corners transformed by the world space matrix.
    */
    _setOBBCorners(box, matrix) {
        const min = box.min;
        const max = box.max;

        _tmpBoxPoints[0].set(min.x, min.y, min.z).applyMatrix4(matrix);
        _tmpBoxPoints[1].set(min.x, min.y, max.z).applyMatrix4(matrix);
        _tmpBoxPoints[2].set(min.x, max.y, min.z).applyMatrix4(matrix);
        _tmpBoxPoints[3].set(min.x, max.y, max.z).applyMatrix4(matrix);
        _tmpBoxPoints[4].set(max.x, min.y, min.z).applyMatrix4(matrix);
        _tmpBoxPoints[5].set(max.x, min.y, max.z).applyMatrix4(matrix);
        _tmpBoxPoints[6].set(max.x, max.y, min.z).applyMatrix4(matrix);
        _tmpBoxPoints[7].set(max.x, max.y, max.z).applyMatrix4(matrix);
    }

    /**
     * Projects the temporary bounding box points from world space to 2D NDC via the camera.
     */
    _projectOBBCorners(camera) {
        for (let i = 0; i < 8; i++) {
            _tmpBoxPoints[i].project(camera);
        }
    }

    /**
     * Checks if two 2D segments (ab and cd) intersect.
     * Used for OBB intersection tests.
     */
    _segmentsIntersect2D(ax, ay, bx, by, cx, cy, dx, dy) {
        const dxAB = bx - ax, dyAB = by - ay;
        const dxCD = dx - cx, dyCD = dy - cy;
        const denom = dxAB * dyCD - dyAB * dxCD;
        if (Math.abs(denom) < 1e-10) { return false; }
        const t = ((cx - ax) * dyCD - (cy - ay) * dxCD) / denom;
        const u = ((cx - ax) * dyAB - (cy - ay) * dxAB) / denom;
        return t >= 0 && t <= 1 && u >= 0 && u <= 1;
    }

    /**
     * Checks whether all 8 projected OBB corners are inside the selection box.
     * Used for LTR selection quick-accept.
     */
    _allCornersInRect2D(rx0, ry0, rx1, ry1) {
        for (let i = 0; i < 8; i++) {
            const p = _tmpBoxPoints[i];
            if (p.x < rx0 || p.x > rx1 || p.y < ry0 || p.y > ry1) { return false; }
        }
        return true;
    }

    /**
     * Determines if the projected OBB (object bounding box) intersects the selection box.
     * Used for both LTR (quick reject) and RTL (quick accept) passes.
     *
     * Returns true if any of the following are true:
     *   (a) Any projected OBB corner is inside the selection box
     *   (b) Any of the 12 OBB edges crosses the selection box boundary (segment-segment intersection)
     *   (c) Any selection box corner is inside the OBB's convex hull (computed with Jarvis march)
     *
     * The convex hull logic (c) is robust to non-convex projections (e.g. at high camera angles or zoom).
     */
    _obbIntersectsRect2D(rx0, ry0, rx1, ry1) {
        // Compute the axis-aligned hulls of all projected OBB corners
        let obbMinX = Infinity, obbMaxX = -Infinity, obbMinY = Infinity, obbMaxY = -Infinity;
        for (let i = 0; i < 8; i++) {
            const p = _tmpBoxPoints[i];
            if (p.x < obbMinX) { obbMinX = p.x; }
            if (p.x > obbMaxX) { obbMaxX = p.x; }
            if (p.y < obbMinY) { obbMinY = p.y; }
            if (p.y > obbMaxY) { obbMaxY = p.y; }
        }

        // If this hull doesn't overlap the selection box, no case (a/b/c) can succeed
        if (obbMaxX < rx0 || obbMinX > rx1 || obbMaxY < ry0 || obbMinY > ry1) {
            return false;
        }

        // a)
        for (let i = 0; i < 8; i++) {
            const p = _tmpBoxPoints[i];
            if (p.x >= rx0 && p.x <= rx1 && p.y >= ry0 && p.y <= ry1) { return true; }
        }

        // b)
        for (let e = 0; e < _obbEdgePairs.length; e += 2) {
            const a = _tmpBoxPoints[_obbEdgePairs[e]];
            const b = _tmpBoxPoints[_obbEdgePairs[e + 1]];
            const ax = a.x, ay = a.y, bx = b.x, by = b.y;
            if (
                this._segmentsIntersect2D(ax, ay, bx, by, rx0, ry0, rx1, ry0) || // top
                this._segmentsIntersect2D(ax, ay, bx, by, rx1, ry0, rx1, ry1) || // right
                this._segmentsIntersect2D(ax, ay, bx, by, rx1, ry1, rx0, ry1) || // bottom
                this._segmentsIntersect2D(ax, ay, bx, by, rx0, ry1, rx0, ry0) // left
            ) {
                return true;
            }
        }

        // c)
        this._buildConvexHull();
        if (
            this._pointInConvexHull(rx0, ry0) ||
            this._pointInConvexHull(rx1, ry0) ||
            this._pointInConvexHull(rx1, ry1) ||
            this._pointInConvexHull(rx0, ry1)
        ) {
            return true;
        }

        return false;
    }

    /**
     * Builds the convex hull of the 8 projected OBB corners using Jarvis march (gift wrapping).
     * Stores hull vertex indices in _convexHullBuffer[0.._convexHullSize-1].
     * This is robust to non-convex projections (e.g. some corners inside the hull of others).
     */
    _buildConvexHull() {
        _convexHullSize = 0;

        // Find the nearest point to the left side (then to the bottom) as the start
        let start = 0;
        for (let i = 1; i < 8; i++) {
            const pi = _tmpBoxPoints[i], ps = _tmpBoxPoints[start];
            if (pi.x < ps.x || (pi.x === ps.x && pi.y < ps.y)) { start = i; }
        }

        let current = start;
        do {
            _convexHullBuffer[_convexHullSize++] = current;
            // Pick an initial candidate that is not current
            let next = current === 0 ? 1 : 0;
            for (let i = 0; i < 8; i++) {
                if (i === current) { continue; }
                const ax = _tmpBoxPoints[current].x, ay = _tmpBoxPoints[current].y;
                const bx = _tmpBoxPoints[next].x, by = _tmpBoxPoints[next].y;
                const cx = _tmpBoxPoints[i].x, cy = _tmpBoxPoints[i].y;

                // (next - current) × (i - current): negative -> i is more CW than next -> update
                const cross = (bx - ax) * (cy - ay) - (by - ay) * (cx - ax);
                if (cross < 0 || (cross === 0 &&
                    (cx - ax) * (cx - ax) + (cy - ay) * (cy - ay) >
                    (bx - ax) * (bx - ax) + (by - ay) * (by - ay))) {
                    next = i;
                }
            }
            current = next;
        } while (current !== start && _convexHullSize < 8);
    }

    /**
     * Checks whether (px, py) is inside (or on the boundary of) the convex hull built by _buildConvexHull().
     */
    _pointInConvexHull(px, py) {
        for (let i = 0; i < _convexHullSize; i++) {
            const a = _tmpBoxPoints[_convexHullBuffer[i]];
            const b = _tmpBoxPoints[_convexHullBuffer[(i + 1) % _convexHullSize]];
            const edgeX = b.x - a.x, edgeY = b.y - a.y;

            // Skip degenerate edges (coincident projected corners, e.g. thin panel viewed face-on).
            if (edgeX * edgeX + edgeY * edgeY < 1e-20) { continue; }

            // CCW cross product: positive = left of edge = inside. Negative = outside.
            if (edgeX * (py - a.y) - edgeY * (px - a.x) < -1e-10) { return false; }
        }
        return true;
    }

    /**
     * Vertex-sampling hit test for non-batched geometry (InstancedMesh or regular mesh).
     * Projects up to maxSamples evenly-strided vertices through worldMatrix and camera, testing each NDC position against the selection rect.
     *
     * - left-to-right (strictMode=true): returns true only if all sampled vertices are inside
     * - right-to-left (strictMode=false): returns true as soon as any sampled vertex is inside
     *
     * Returns null if the geometry has no position attribute.
     */
    _testVertexSampling(geometry, worldMatrix, rx0, ry0, rx1, ry1, strictMode, maxSamples = MAX_VERTEX_COUNT) {
        const position = geometry.attributes?.position;
        if (!position || position.count === 0) { return null; }

        const count = position.count;
        const step = count <= maxSamples ? 1 : Math.floor(count / maxSamples);

        for (let i = 0; i < count; i += step) {
            _tmpPoint.fromBufferAttribute(position, i).applyMatrix4(worldMatrix);
            _tmpPoint.project(this.camera);
            const isInside = _tmpPoint.x >= rx0 && _tmpPoint.x <= rx1 && _tmpPoint.y >= ry0 && _tmpPoint.y <= ry1;

            if (strictMode) {
                if (!isInside) { return false; } // left-to-right: first vertex outside -> not selected
            } else {
                if (isInside) { return true; } // right-to-left: first vertex inside -> selected
            }
        }

        return strictMode;
    }

    /**
     * Vertex-sampling hit test for a specific geometry slot inside a BatchedMesh.
     * Reads directly from the shared position buffer using the per-geometry vertex range in _geometryInfo.
     *
     * - left-to-right (strictMode=true): returns true only if all sampled vertices are inside
     * - right-to-left (strictMode=false): returns true as soon as any sampled vertex is inside
     *
     * Returns null if geometry info or position attribute is unavailable.
     */
    _testVertexSamplingBatched(object, geometryIndex, worldMatrix, rx0, ry0, rx1, ry1, strictMode, maxSamples = MAX_VERTEX_COUNT) {
        const position = object.geometry?.attributes?.position;
        if (!position) { return null; }

        const geomInfo = object._geometryInfo?.[geometryIndex];
        if (!geomInfo || geomInfo.vertexStart === undefined || geomInfo.vertexCount === undefined) { return null; }

        const start = geomInfo.vertexStart;
        const total = geomInfo.vertexCount;
        if (total === 0) { return null; }

        const step = total <= maxSamples ? 1 : Math.floor(total / maxSamples);

        for (let i = start; i < start + total; i += step) {
            _tmpPoint.fromBufferAttribute(position, i).applyMatrix4(worldMatrix);
            _tmpPoint.project(this.camera);
            const isInside = _tmpPoint.x >= rx0 && _tmpPoint.x <= rx1 && _tmpPoint.y >= ry0 && _tmpPoint.y <= ry1;

            if (strictMode) {
                if (!isInside) { return false; } // left-to-right: first vertex outside -> not selected
            } else {
                if (isInside) { return true; } // right-to-left: first vertex inside -> selected
            }
        }
        return strictMode;
    }

    /**
     * 2D Separating Axis Theorem (SAT) test between a projected triangle and the selection box.
     * Tests 5 axes: X, Y, and the outward normals of each triangle edge.
     * Returns false as soon as a separating axis is found (no overlap), true if all axes overlap.
     * Used for RTL selection triangle SAT fallback.
     */
    _triangleIntersectsRect2D(ax, ay, bx, by, cx, cy, rx0, ry0, rx1, ry1) {
        // Axis 1: X
        if (Math.max(ax, bx, cx) < rx0 || Math.min(ax, bx, cx) > rx1) {
            return false;
        }

        // Axis 2: Y
        if (Math.max(ay, by, cy) < ry0 || Math.min(ay, by, cy) > ry1) {
            return false;
        }

        // Axes 3–5: edge normals. Normal for p1 to p2 is (-(p2y-p1y), p2x-p1x)
        let nx, ny, dA, dB, dC, tMin, tMax, rMin, rMax;

        // Edge AB
        nx = ay - by; ny = bx - ax;
        if (nx * nx + ny * ny > 1e-28) {
            dA = nx * ax + ny * ay; dB = nx * bx + ny * by; dC = nx * cx + ny * cy;
            tMin = Math.min(dA, dB, dC); tMax = Math.max(dA, dB, dC);
            rMin = Math.min(nx * rx0 + ny * ry0, nx * rx1 + ny * ry0, nx * rx1 + ny * ry1, nx * rx0 + ny * ry1);
            rMax = Math.max(nx * rx0 + ny * ry0, nx * rx1 + ny * ry0, nx * rx1 + ny * ry1, nx * rx0 + ny * ry1);
            if (tMax < rMin || tMin > rMax) {
                return false;
            }
        }

        // Edge BC
        nx = by - cy; ny = cx - bx;
        if (nx * nx + ny * ny > 1e-28) {
            dA = nx * ax + ny * ay; dB = nx * bx + ny * by; dC = nx * cx + ny * cy;
            tMin = Math.min(dA, dB, dC); tMax = Math.max(dA, dB, dC);
            rMin = Math.min(nx * rx0 + ny * ry0, nx * rx1 + ny * ry0, nx * rx1 + ny * ry1, nx * rx0 + ny * ry1);
            rMax = Math.max(nx * rx0 + ny * ry0, nx * rx1 + ny * ry0, nx * rx1 + ny * ry1, nx * rx0 + ny * ry1);
            if (tMax < rMin || tMin > rMax) {
                return false;
            }
        }

        // Edge CA
        nx = cy - ay; ny = ax - cx;
        if (nx * nx + ny * ny > 1e-28) {
            dA = nx * ax + ny * ay; dB = nx * bx + ny * by; dC = nx * cx + ny * cy;
            tMin = Math.min(dA, dB, dC); tMax = Math.max(dA, dB, dC);
            rMin = Math.min(nx * rx0 + ny * ry0, nx * rx1 + ny * ry0, nx * rx1 + ny * ry1, nx * rx0 + ny * ry1);
            rMax = Math.max(nx * rx0 + ny * ry0, nx * rx1 + ny * ry0, nx * rx1 + ny * ry1, nx * rx0 + ny * ry1);
            if (tMax < rMin || tMin > rMax) {
                return false;
            }
        }

        return true;
    }

    /**
     * RTL (right-to-left) triangle SAT test: iterates triangles of the geometry and returns true if any projected triangle intersects the selection box.
     * Handles both indexed and non-indexed geometry. Caps triangle count for performance.
     * Returns null if geometry data is unavailable (caller falls back to OBB logic).
     */
    _testTrianglesTouch(geometry, worldMatrix, rx0, ry0, rx1, ry1) {
        const position = geometry.attributes?.position;
        if (!position || position.count === 0) { return null; }

        const index = geometry.index;
        const triangleCount = index ? Math.floor(index.count / 3) : Math.floor(position.count / 3);
        if (triangleCount === 0) { return null; }

        // Adaptive stride for performance
        const step = triangleCount <= MAX_TRIANGLE_COUNT ? 1 : Math.floor(triangleCount / MAX_TRIANGLE_COUNT);

        for (let t = 0; t < triangleCount; t += step) {
            const base = t * 3;
            const i0 = index ? index.getX(base) : base;
            const i1 = index ? index.getX(base + 1) : base + 1;
            const i2 = index ? index.getX(base + 2) : base + 2;

            _tmpPoint.fromBufferAttribute(position, i0).applyMatrix4(worldMatrix);
            _tmpPoint.project(this.camera);
            const ax = _tmpPoint.x, ay = _tmpPoint.y;

            _tmpPoint.fromBufferAttribute(position, i1).applyMatrix4(worldMatrix);
            _tmpPoint.project(this.camera);
            const bx = _tmpPoint.x, by = _tmpPoint.y;

            _tmpPoint.fromBufferAttribute(position, i2).applyMatrix4(worldMatrix);
            _tmpPoint.project(this.camera);
            const cx = _tmpPoint.x, cy = _tmpPoint.y;

            if (this._triangleIntersectsRect2D(ax, ay, bx, by, cx, cy, rx0, ry0, rx1, ry1)) { return true; }
        }
        return false;
    }

    /**
     * RTL (right-to-left) triangle SAT test for a geometry slot inside a BatchedMesh.
     * Reads index and vertex ranges from _geometryInfo. Falls back to non-indexed if no shared index buffer.
     * Returns null if geometry data is unavailable.
     */
    _testTrianglesTouchBatched(object, geometryIndex, worldMatrix, rx0, ry0, rx1, ry1) {
        const position = object.geometry?.attributes?.position;
        if (!position) {
            return null;
        }

        const geomInfo = object._geometryInfo?.[geometryIndex];
        if (!geomInfo) {
            return null;
        }

        const index = object.geometry?.index;

        // Primary path: shared index buffer (standard BatchedMesh layout).
        if (index && geomInfo.indexStart !== undefined && geomInfo.indexCount !== undefined) {
            const triCount = Math.floor(geomInfo.indexCount / 3);
            if (triCount === 0) {
                return null;
            }
            // Adaptive stride for performance
            const step = triCount <= MAX_TRIANGLE_COUNT ? 1 : Math.floor(triCount / MAX_TRIANGLE_COUNT);
            const idxBase = geomInfo.indexStart;

            for (let t = 0; t < triCount; t += step) {
                const base = idxBase + t * 3;
                const i0 = index.getX(base), i1 = index.getX(base + 1), i2 = index.getX(base + 2);

                _tmpPoint.fromBufferAttribute(position, i0).applyMatrix4(worldMatrix);
                _tmpPoint.project(this.camera);
                const ax = _tmpPoint.x, ay = _tmpPoint.y;

                _tmpPoint.fromBufferAttribute(position, i1).applyMatrix4(worldMatrix);
                _tmpPoint.project(this.camera);
                const bx = _tmpPoint.x, by = _tmpPoint.y;

                _tmpPoint.fromBufferAttribute(position, i2).applyMatrix4(worldMatrix);
                _tmpPoint.project(this.camera);
                const cx = _tmpPoint.x, cy = _tmpPoint.y;

                if (this._triangleIntersectsRect2D(ax, ay, bx, by, cx, cy, rx0, ry0, rx1, ry1)) { return true; }
            }

            return false;
        }

        // Fallback: no shared index — treat vertex range as non-indexed triangles.
        if (geomInfo.vertexStart !== undefined && geomInfo.vertexCount !== undefined) {
            const start = geomInfo.vertexStart;
            const triCount = Math.floor(geomInfo.vertexCount / 3);
            if (triCount === 0) {
                return null;
            }
            // Adaptive stride for performance
            const step = triCount <= MAX_TRIANGLE_COUNT ? 1 : Math.floor(triCount / MAX_TRIANGLE_COUNT);

            for (let t = 0; t < triCount; t += step) {
                const i0 = start + t * 3, i1 = i0 + 1, i2 = i0 + 2;

                _tmpPoint.fromBufferAttribute(position, i0).applyMatrix4(worldMatrix);
                _tmpPoint.project(this.camera);
                const ax = _tmpPoint.x, ay = _tmpPoint.y;

                _tmpPoint.fromBufferAttribute(position, i1).applyMatrix4(worldMatrix);
                _tmpPoint.project(this.camera);
                const bx = _tmpPoint.x, by = _tmpPoint.y;

                _tmpPoint.fromBufferAttribute(position, i2).applyMatrix4(worldMatrix);
                _tmpPoint.project(this.camera);
                const cx = _tmpPoint.x, cy = _tmpPoint.y;

                if (this._triangleIntersectsRect2D(ax, ay, bx, by, cx, cy, rx0, ry0, rx1, ry1)) {
                    return true;
                }
            }
            return false;
        }

        return null;
    }

    /**
     * LTR (left-to-right) selection evaluation: returns true if the projected OBB is fully enclosed by the selection box.
     * Uses quick-accept (all corners in rect), quick-reject (no intersection), then vertex sampling.
     */
    _evaluateLTR(geometry, worldMatrix, baseBox, rx0, ry0, rx1, ry1) {
        // 1. Quick accept: If all 8 OBB (Object Bounding Box) corners are inside the selection box, select immediately.
        if (this._allCornersInRect2D(rx0, ry0, rx1, ry1)) { return true; }

        // 2. Quick reject: If the OBB doesn’t intersect with the selection box, deselect/reject immediately.
        if (!this._obbIntersectsRect2D(rx0, ry0, rx1, ry1)) { return false; }

        // 3. Fallback for complex geometries: vertex sampling
        const sampled = this._testVertexSampling(
            geometry, worldMatrix, rx0, ry0, rx1, ry1, true, MAX_GEOMETRY_COUNT
        );
        if (sampled !== null) { return sampled; }

        return false;
    }

    /**
     * RTL (right-to-left) selection evaluation: returns true if the projected OBB is partially enclosed by the selection box.
     * Uses OBB intersection, vertex sampling, and triangle SAT fallback.
     */
    _evaluateRTL(geometry, worldMatrix, baseBox, rx0, ry0, rx1, ry1) {
        // Quick pre-filter: skip geometry tests for objects that are clearly outside the selection box
        if (!this._obbIntersectsRect2D(rx0, ry0, rx1, ry1)) { return false; }

        // 1. Vertex sampling: If any vertex is inside the selection box, select immediately
        const vResult = this._testVertexSampling(
            geometry, worldMatrix, rx0, ry0, rx1, ry1, false, MAX_GEOMETRY_COUNT
        );
        if (vResult === true) { return true; }
        if (vResult === null) { return true; }

        // 2. Triangle SAT (Separating Axis Theorem): Covers face intersections where no vertex is inside the rect.
        const triResult = this._testTrianglesTouch(
            geometry, worldMatrix, rx0, ry0, rx1, ry1, MAX_GEOMETRY_COUNT
        );
        return triResult !== null ? triResult : false;
    }

    /**
     * RTL (right-to-left) selection evaluation for BatchedMesh: returns true if the projected OBB is partially enclosed by the selection box.
     * Uses OBB intersection, vertex sampling, and triangle SAT fallback.
     */
    _evaluateRTLBatched(object, geometryIndex, worldMatrix, baseBox, rx0, ry0, rx1, ry1) {
        if (!this._obbIntersectsRect2D(rx0, ry0, rx1, ry1)) {
            return false;
        }

        const vResult = this._testVertexSamplingBatched(
            object, geometryIndex, worldMatrix, rx0, ry0, rx1, ry1, false, MAX_GEOMETRY_COUNT
        );
        if (vResult === true) { return true; }
        if (vResult === null) { return true; }

        const triResult = this._testTrianglesTouchBatched(
            object, geometryIndex, worldMatrix, rx0, ry0, rx1, ry1, MAX_GEOMETRY_COUNT
        );
        return triResult !== null ? triResult : false;
    }

    /**
     * LTR (left-to-right) selection evaluation for BatchedMesh: returns true if the projected OBB is fully enclosed by the selection box.
     * Uses quick-accept (all corners in rect), quick-reject (no intersection), then vertex sampling.
     */
    _evaluateLTRBatched(object, geometryIndex, worldMatrix, baseBox, rx0, ry0, rx1, ry1) {
        if (this._allCornersInRect2D(rx0, ry0, rx1, ry1)) { return true; }
        if (!this._obbIntersectsRect2D(rx0, ry0, rx1, ry1)) { return false; }

        const sampled = this._testVertexSamplingBatched(
            object, geometryIndex, worldMatrix, rx0, ry0, rx1, ry1, true, MAX_GEOMETRY_COUNT
        );
        if (sampled !== null) { return sampled; }

        return false;
    }

    _searchChildInFrustum(frustum, object) {
        if (object.isMesh || object.isLine || object.isPoints) {
            if (object.isInstancedMesh) {
                this.instances[object.uuid] = [];

                if (object.geometry.boundingBox === null) {
                    object.geometry.computeBoundingBox();
                }

                const baseBox = object.geometry.boundingBox;
                const rx0 = this._selectionMinX, ry0 = this._selectionMinY;
                const rx1 = this._selectionMaxX, ry1 = this._selectionMaxY;

                for (let instanceId = 0; instanceId < object.count; instanceId++) {
                    object.getMatrixAt(instanceId, _matrix);

                    _worldMatrix.multiplyMatrices(object.matrixWorld, _matrix);

                    // 3D frustum pre-cull: skip objects that are clearly outside the selection box
                    _tmpWorldBox.copy(baseBox).applyMatrix4(_worldMatrix);
                    if (!this.frustum.intersectsBox(_tmpWorldBox)) { continue; }

                    this._setOBBCorners(baseBox, _worldMatrix);
                    this._projectOBBCorners(this.camera);

                    if (this.isLeftToRight) {
                        if (this._evaluateLTR(object.geometry, _worldMatrix, baseBox, rx0, ry0, rx1, ry1)) {
                            this.instances[object.uuid].push(instanceId);
                        }
                    } else {
                        if (this._evaluateRTL(object.geometry, _worldMatrix, baseBox, rx0, ry0, rx1, ry1)) {
                            this.instances[object.uuid].push(instanceId);
                        }
                    }
                }
            } else if (object.isBatchedMesh) {
                this.batches[object.uuid] = [];

                const instanceInfo = object._instanceInfo;
                if (!instanceInfo) {
                    return;
                }

                const maxSlots = instanceInfo.length;
                const boundingBoxCache = {};
                const rx0 = this._selectionMinX, ry0 = this._selectionMinY;
                const rx1 = this._selectionMaxX, ry1 = this._selectionMaxY;

                for (let instanceId = 0; instanceId < maxSlots; instanceId++) {
                    const info = instanceInfo[instanceId];
                    if (!info || !info.active) {
                        continue;
                    }

                    // right to left only: skip invisible instances to prevent false positives.
                    if (!this.isLeftToRight && !info.visible) {
                        continue;
                    }

                    const geometryIndex = info.geometryIndex;
                    object.getMatrixAt(instanceId, _matrix);

                    // retrieve or cache the per-geometry bounding box
                    let cachedBox = boundingBoxCache[geometryIndex];
                    if (!cachedBox) {
                        cachedBox = new Box3();
                        object.getBoundingBoxAt(geometryIndex, cachedBox);
                        boundingBoxCache[geometryIndex] = cachedBox;
                    }

                    _worldMatrix.multiplyMatrices(object.matrixWorld, _matrix);

                    // 3D frustum pre-cull: skip objects that are clearly outside the selection box
                    _tmpWorldBox.copy(cachedBox).applyMatrix4(_worldMatrix);
                    if (!this.frustum.intersectsBox(_tmpWorldBox)) {
                        continue;
                    }

                    this._setOBBCorners(cachedBox, _worldMatrix);
                    this._projectOBBCorners(this.camera);

                    if (this.isLeftToRight) {
                        if (this._evaluateLTRBatched(object, geometryIndex, _worldMatrix, cachedBox, rx0, ry0, rx1, ry1)) {
                            this.batches[object.uuid].push(instanceId);
                        }
                    } else {
                        if (this._evaluateRTLBatched(object, geometryIndex, _worldMatrix, cachedBox, rx0, ry0, rx1, ry1)) {
                            this.batches[object.uuid].push(instanceId);
                        }
                    }
                }
            } else {
                const geometry = object.geometry;

                if (geometry.boundingBox === null) {
                    geometry.computeBoundingBox();
                }

                const baseBox = geometry.boundingBox;
                const rx0 = this._selectionMinX, ry0 = this._selectionMinY;
                const rx1 = this._selectionMaxX, ry1 = this._selectionMaxY;

                this._setOBBCorners(baseBox, object.matrixWorld);
                this._projectOBBCorners(this.camera);

                if (this.isLeftToRight) {
                    if (this._evaluateLTR(geometry, object.matrixWorld, baseBox, rx0, ry0, rx1, ry1)) {
                        this.collection.push(object);
                    }
                } else {
                    if (this._evaluateRTL(geometry, object.matrixWorld, baseBox, rx0, ry0, rx1, ry1)) {
                        this.collection.push(object);
                    }
                }
            }
        }

        if (object.children.length > 0) {
            for (let x = 0; x < object.children.length; x++) {
                this._searchChildInFrustum(frustum, object.children[x]);
            }
        }
    }
}

export { SelectionBox };